import React, { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, Map, ChevronDown, ChevronRight, UserPlus, X, Pencil, Check, Search } from 'lucide-react';
import { AccessDeniedDialog } from '../components/common/AccessDeniedDialog';
import {
  useGetZonesQuery,
  useCreateZoneMutation,
  useDeleteZoneMutation,
  useAssignSensorMutation,
  useRemoveSensorMutation,
  useUpdateZoneUsersMutation,
  useUpdateZoneMutation,
  useGetZoneSensorsQuery,
} from '../services/zoneApi';
import { useGetSensorListQuery } from '../services/sensorApi';
import { useGetUsersQuery } from '../services/userApi';
import ZoneSensors from '../components/zones/ZoneSensors';
import { usePermissions } from '../hooks/usePermissions';
import { useSelector } from 'react-redux';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { formatSensorSerial } from '../utils/sensorUtils';
import { normalizeZoneAssignedUserIds, isUserAssignedToZone } from '../utils/zoneUserIds';
import type { RootState } from '../store/store';

interface ZoneFormData {
  name: string;
}

const initialFormState: ZoneFormData = {
  name: '',
};

function getAssignedUserIds(zone: unknown): number[] {
  if (!zone || typeof zone !== 'object') return [];
  return normalizeZoneAssignedUserIds((zone as { zone_users?: unknown }).zone_users);
}

function getUserCount(zone: any): number {
  return getAssignedUserIds(zone).length;
}

export default function ZonesPage() {
  const { canManageZones } = usePermissions();
  const [accessDeniedAction, setAccessDeniedAction] = useState<string | null>(null);
  const user = useSelector((state: RootState) => state.auth.user);
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [expandedZones, setExpandedZones] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<ZoneFormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedZoneForUsers, setSelectedZoneForUsers] = useState<number | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string>('');
  const [isSubmittingZone, setIsSubmittingZone] = useState(false);
  const [editingZoneId, setEditingZoneId] = useState<number | null>(null);
  const [editingZoneName, setEditingZoneName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const [quickAddZoneId, setQuickAddZoneId] = useState<number | null>(null);
  const [quickAddSearch, setQuickAddSearch] = useState('');
  const [quickAddSelected, setQuickAddSelected] = useState<Set<number>>(new Set());
  const [quickAddBusy, setQuickAddBusy] = useState(false);

  const { data: zones, isLoading: zonesLoading, error: zonesError } = useGetZonesQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: sensors } = useGetSensorListQuery();
  const [createZone] = useCreateZoneMutation();
  const [updateZone] = useUpdateZoneMutation();
  const [deleteZone] = useDeleteZoneMutation();
  const [assignSensor] = useAssignSensorMutation();
  const [removeSensor] = useRemoveSensorMutation();
  const [updateZoneUsers, { isLoading: isSavingZoneUsers }] = useUpdateZoneUsersMutation();
  const { data: users, isLoading: usersLoading } = useGetUsersQuery(undefined, {
    skip: !selectedZoneForUsers
  });
  const { data: quickAddZoneSensors } = useGetZoneSensorsQuery(quickAddZoneId ?? 0, {
    skip: !quickAddZoneId,
  });
  const quickAddAssignedIds = React.useMemo(
    () => new Set((quickAddZoneSensors ?? []).map(s => Number(s.sensor_id))),
    [quickAddZoneSensors]
  );
  const quickAddUnassigned = React.useMemo(() => {
    if (!sensors) return [];
    const q = quickAddSearch.toLowerCase();
    return sensors.filter(s =>
      !quickAddAssignedIds.has(Number(s.id)) &&
      (!q || s.name.toLowerCase().includes(q))
    );
  }, [sensors, quickAddAssignedIds, quickAddSearch]);
  const quickAddAllSelected = quickAddUnassigned.length > 0 && quickAddUnassigned.every(s => quickAddSelected.has(s.id));

  useEffect(() => {
    setQuickAddSelected(new Set());
  }, [quickAddZoneId]);

  const toggleQuickAddSensor = (sensorId: number) => {
    setQuickAddSelected(prev => {
      const next = new Set(prev);
      if (next.has(sensorId)) next.delete(sensorId); else next.add(sensorId);
      return next;
    });
  };

  const toggleQuickAddAll = () => {
    setQuickAddSelected(prev => {
      const next = new Set(prev);
      if (quickAddAllSelected) quickAddUnassigned.forEach(s => next.delete(s.id));
      else quickAddUnassigned.forEach(s => next.add(s.id));
      return next;
    });
  };

  useEffect(() => {
    setPendingUserId('');
  }, [selectedZoneForUsers]);

  const toggleZone = (zoneId: number) => {
    const newExpanded = new Set(expandedZones);
    if (expandedZones.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageZones) { setAccessDeniedAction('Creating zones'); return; }
    if (isSubmittingZone) return;
    setFormError(null);
    setIsSubmittingZone(true);

    try {
      const newZone = await createZone({ zoneName: formData.name }).unwrap();
      // Zone is added to cache immediately via onQueryStarted in zoneApi

      const zoneId = newZone?.zone_id;
      if (user?.id && zoneId) {
        try {
          await updateZoneUsers({
            zoneId: zoneId,
            userIds: [user.id]
          }).unwrap();
        } catch (assignError) {
          console.error('Failed to auto-assign creator to zone:', assignError);
        }
      }
      
      const { store } = await import('../store/store');
      const state = store.getState();
      const freshUser = state.auth.user;
      
      await logAuditEvent(
        AUDIT_ACTIONS.ZONE_CREATE,
        `Zone created - Name: ${formData.name}`,
        freshUser || user
      );
      
      setFormData(initialFormState);
      setIsAddingZone(false);
    } catch (error) {
      setFormError('Failed to create zone. Please try again.');
    } finally {
      setIsSubmittingZone(false);
    }
  };

  const handleDelete = async (zoneId: number) => {
    if (!canManageZones) { setAccessDeniedAction('Deleting zones'); return; }
    if (window.confirm('Are you sure you want to delete this zone?')) {
      try {
        setFormError(null);
        const zoneToDelete = zones?.find(z => z.zone_id === zoneId);
        await deleteZone(zoneId).unwrap();
        
        // Log zone deletion
        await logAuditEvent(
          AUDIT_ACTIONS.ZONE_DELETE,
          `Zone deleted - Zone ID: ${zoneId}, Name: ${zoneToDelete?.zone_name || 'unknown'}`,
          user // Pass the current user data
        );
        
        setExpandedZones(prev => {
          const newSet = new Set(prev);
          newSet.delete(zoneId);
          return newSet;
        });
      } catch (error: any) {
        console.error('Failed to delete zone:', error);
        setFormError(error?.data?.message || 'Failed to delete zone. Please try again.');
      }
    }
  };

  const handleStartEdit = (zone: { zone_id: number; zone_name: string }) => {
    if (!canManageZones) { setAccessDeniedAction('Editing zones'); return; }
    setEditingZoneId(zone.zone_id);
    setEditingZoneName(zone.zone_name);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const handleCancelEdit = () => {
    setEditingZoneId(null);
    setEditingZoneName('');
  };

  const handleSaveEdit = async (zoneId: number) => {
    const trimmed = editingZoneName.trim();
    if (!trimmed) return;
    try {
      setFormError(null);
      await updateZone({ zoneId, zoneName: trimmed }).unwrap();
      await logAuditEvent(AUDIT_ACTIONS.ZONE_EDIT, `Zone renamed - Zone ID: ${zoneId}, New name: ${trimmed}`, user);
      setEditingZoneId(null);
      setEditingZoneName('');
    } catch (error: any) {
      setFormError(error?.data?.message || 'Failed to rename zone.');
    }
  };

  const handleAssignSensor = async (zoneId: number, sensorId: number) => {
    if (!canManageZones) { setAccessDeniedAction('Managing zone sensors'); return; }
    try {
      setFormError(null);
      if (!zoneId || !sensorId) {
        throw new Error('Zone ID and sensor ID are required');
      }
      const sensor = sensors?.find(s => s.id === sensorId);
      await assignSensor({
        zoneId,
        sensorId,
        sensorName: sensor?.name,
        sensorSerialNo: sensor?.serialNo,
      }).unwrap();
    } catch (error) {
      console.error('Failed to assign sensor:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to assign sensor to zone');
    }
  };

  const handleRemoveSensor = async (zoneId: number, sensorId: number) => {
    if (!canManageZones) { setAccessDeniedAction('Managing zone sensors'); return; }
    try {
      setFormError(null);
      if (!zoneId || !sensorId) {
        throw new Error('Zone ID and sensor ID are required');
      }
      await removeSensor({ zoneId, sensorId }).unwrap();
    } catch (error) {
      console.error('Failed to remove sensor:', error);
      setFormError(error instanceof Error ? error.message : 'Failed to remove sensor from zone');
    }
  };

  const handleAssignUser = async (userIds: number[]) => {
    if (!selectedZoneForUsers) return;
    try {
      const currentZone = zones?.find((z) => z.zone_id === selectedZoneForUsers);
      const currentUserIds = getAssignedUserIds(currentZone);
      const mergedUserIds = [...new Set([...currentUserIds, ...userIds])];

      await updateZoneUsers({
        zoneId: selectedZoneForUsers,
        userIds: mergedUserIds,
      }).unwrap();
    } catch (error) {
      console.error('Failed to assign user:', error);
      setFormError('Failed to assign user to zone');
    }
  };

  const handleAddPendingUserToZone = async () => {
    if (!selectedZoneForUsers || pendingUserId === '') return;
    const id = Number(pendingUserId);
    if (!Number.isFinite(id)) return;
    setFormError(null);
    setPendingUserId('');
    await handleAssignUser([id]);
  };

  const handleRemoveUser = async (userId: number) => {
    if (!selectedZoneForUsers) return;
    try {
      const currentZone = zones?.find((z) => z.zone_id === selectedZoneForUsers);
      const currentUserIds = getAssignedUserIds(currentZone);
      const newUserIds = currentUserIds.filter((id) => id !== userId);

      await updateZoneUsers({
        zoneId: selectedZoneForUsers,
        userIds: newUserIds,
      }).unwrap();
    } catch (error) {
      console.error('Failed to remove user:', error);
      setFormError('Failed to remove user from zone');
    }
  };

  if (zonesLoading) {
    return <div className="p-6">Loading zones...</div>;
  }

  if (zonesError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load zones</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Zones</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            {canManageZones ? 'Manage your zones' : 'View zones'}
          </p>
        </div>
        <button
          onClick={() => {
            if (!canManageZones) { setAccessDeniedAction('Adding zones'); return; }
            setIsAddingZone(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-md flex items-center text-sm"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          <span className="hidden sm:inline">Add Zone</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {formError && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 p-4 rounded-md">
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        </div>
      )}

      {isAddingZone && (
        <div className="mb-6 bg-app-panel dark:bg-gray-800 shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add New Zone</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Zone Name
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsAddingZone(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingZone}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmittingZone ? 'Creating…' : 'Create Zone'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Mobile: card list ── */}
      <div className="md:hidden space-y-2">
        {zones?.map((zone) => (
          <div key={zone.zone_id} className="bg-app-panel dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
            <div className="px-4 py-3 cursor-pointer select-none" onClick={() => toggleZone(zone.zone_id)}>
              <div className="flex items-center justify-between gap-2 min-w-0">
                {/* Expand + name */}
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="p-1 flex-shrink-0">
                    {expandedZones.has(zone.zone_id)
                      ? <ChevronDown className="h-4 w-4 text-gray-400" />
                      : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </span>
                  {editingZoneId === zone.zone_id ? (
                    <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(zone.zone_id); }} onClick={e => e.stopPropagation()} className="flex items-center gap-1 flex-1">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editingZoneName}
                        onChange={(e) => setEditingZoneName(e.target.value)}
                        className="text-sm font-medium border border-indigo-400 rounded px-2 py-0.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 min-w-0"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit()}
                      />
                      <button type="submit" className="p-1 text-green-600 dark:text-green-400 flex-shrink-0"><Check className="h-4 w-4" /></button>
                      <button type="button" onClick={handleCancelEdit} className="p-1 text-gray-400 flex-shrink-0"><X className="h-4 w-4" /></button>
                    </form>
                  ) : (
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{zone.zone_name}</span>
                  )}
                </div>
                {/* Actions */}
                <div className="flex items-center gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => { if (!canManageZones) { setAccessDeniedAction('Adding sensors to zone'); return; } setQuickAddSearch(''); setQuickAddZoneId(zone.zone_id); }}
                    className="p-1.5 text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300"
                    title="Add sensor to zone"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => { if (!canManageZones) { setAccessDeniedAction('Managing zone users'); return; } setSelectedZoneForUsers(zone.zone_id); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Manage Users"
                  >
                    <UserPlus className="h-4 w-4" />
                  </button>
                  {editingZoneId !== zone.zone_id && (
                    <button onClick={() => handleStartEdit(zone)} className="p-1.5 text-indigo-500 dark:text-indigo-400" title="Rename">
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(zone.zone_id)} className="p-1.5 text-red-500 dark:text-red-400" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {/* Badges */}
              <div className="mt-2 flex items-center gap-2 pl-7">
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
                  {zone.sensor_count} sensor{zone.sensor_count !== 1 ? 's' : ''}
                </span>
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                  {getUserCount(zone)} user{getUserCount(zone) !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            {expandedZones.has(zone.zone_id) && (
              <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <ZoneSensors
                  zoneId={zone.zone_id}
                  sensors={sensors ?? []}
                  onAssign={handleAssignSensor}
                  onRemove={handleRemoveSensor}
                  zonesLoading={zonesLoading}
                />
              </div>
            )}
          </div>
        ))}
        {(!zones || zones.length === 0) && (
          <p className="text-center text-gray-500 dark:text-gray-400 py-8 text-sm">No zones yet. Tap Add to create one.</p>
        )}
      </div>

      {/* ── Desktop: table ── */}
      <div className="hidden md:block bg-app-panel dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Zone Name</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sensors</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-app-panel dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {zones?.map((zone) => (
              <React.Fragment key={zone.zone_id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => toggleZone(zone.zone_id)}>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="p-1 mr-2 flex-shrink-0">
                        {expandedZones.has(zone.zone_id) ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronRight className="h-5 w-5 text-gray-400" />}
                      </span>
                      <Map className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      {editingZoneId === zone.zone_id ? (
                        <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(zone.zone_id); }} onClick={e => e.stopPropagation()} className="flex items-center gap-1">
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editingZoneName}
                            onChange={(e) => setEditingZoneName(e.target.value)}
                            className="text-sm font-medium border border-indigo-400 rounded px-2 py-0.5 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 w-40"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit()}
                          />
                          <button type="submit" className="p-1 text-green-600 dark:text-green-400"><Check className="h-4 w-4" /></button>
                          <button type="button" onClick={handleCancelEdit} className="p-1 text-gray-400"><X className="h-4 w-4" /></button>
                        </form>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{zone.zone_name}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">{zone.sensor_count} sensors</span>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">{getUserCount(zone)} users</span>
                      <button onClick={() => { if (!canManageZones) { setAccessDeniedAction('Managing zone users'); return; } setSelectedZoneForUsers(zone.zone_id); }} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full" title="Manage Users">
                        <UserPlus className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-end space-x-2 items-center">
                      <button
                        onClick={() => { if (!canManageZones) { setAccessDeniedAction('Adding sensors to zone'); return; } setQuickAddSearch(''); setQuickAddZoneId(zone.zone_id); }}
                        className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300 p-1 inline-flex items-center gap-1 text-xs font-medium"
                        title="Add sensor to zone"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Add Sensor</span>
                      </button>
                      {editingZoneId !== zone.zone_id && (
                        <button onClick={() => handleStartEdit(zone)} className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 p-1" title="Rename zone">
                          <Pencil className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(zone.zone_id)} className="text-red-600 hover:text-red-900 dark:text-red-400 p-1" title="Delete zone">
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedZones.has(zone.zone_id) && (
                  <tr>
                    <td colSpan={4} className="bg-gray-50 dark:bg-gray-800/50">
                      <ZoneSensors zoneId={zone.zone_id} sensors={sensors ?? []} onAssign={handleAssignSensor} onRemove={handleRemoveSensor} zonesLoading={zonesLoading} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Quick-add sensor modal */}
      {quickAddZoneId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setQuickAddZoneId(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Add sensors to <span className="text-indigo-600 dark:text-indigo-400">{zones?.find(z => z.zone_id === quickAddZoneId)?.zone_name}</span>
              </h3>
              <button onClick={() => setQuickAddZoneId(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sensors…"
                  value={quickAddSearch}
                  onChange={e => setQuickAddSearch(e.target.value)}
                  autoFocus
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {quickAddUnassigned.length > 1 && (
                <label className="flex items-center gap-2 px-1 pt-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    checked={quickAddAllSelected}
                    onChange={toggleQuickAddAll}
                  />
                  Select all{quickAddSearch ? ' matching' : ''} ({quickAddUnassigned.length})
                </label>
              )}
            </div>
            <ul className="max-h-60 overflow-y-auto py-1">
              {quickAddUnassigned.length === 0 ? (
                <li className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {quickAddSearch ? 'No sensors match your search' : 'All sensors already assigned'}
                </li>
              ) : (
                quickAddUnassigned.map(sensor => (
                  <li key={sensor.id}>
                    <label className="w-full px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-indigo-600 border-gray-300 rounded flex-shrink-0"
                        checked={quickAddSelected.has(sensor.id)}
                        onChange={() => toggleQuickAddSensor(sensor.id)}
                      />
                      <div>
                        <div className="font-medium">{sensor.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatSensorSerial(sensor.serialNo, sensor.type)}</div>
                      </div>
                    </label>
                  </li>
                ))
              )}
            </ul>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setQuickAddZoneId(null)}
                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                disabled={quickAddSelected.size === 0 || quickAddBusy}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-1"
                onClick={async () => {
                  const ids = quickAddUnassigned.filter(s => quickAddSelected.has(s.id)).map(s => s.id);
                  setQuickAddBusy(true);
                  try {
                    for (const sensorId of ids) {
                      await handleAssignSensor(quickAddZoneId, sensorId);
                    }
                  } finally {
                    setQuickAddBusy(false);
                    setQuickAddZoneId(null);
                  }
                }}
              >
                <Plus className="h-3.5 w-3.5" />
                {quickAddBusy
                  ? 'Adding…'
                  : quickAddSelected.size === 0
                    ? 'Add sensors'
                    : `Add ${quickAddSelected.size} sensor${quickAddSelected.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <AccessDeniedDialog
        isOpen={!!accessDeniedAction}
        onClose={() => setAccessDeniedAction(null)}
        action={accessDeniedAction ?? undefined}
      />

      {/* User Management Modal */}
      {selectedZoneForUsers && (
        <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden overscroll-contain bg-black/50 px-4 py-6 flex items-start sm:items-center justify-center">
          <div className="my-auto flex min-h-0 min-w-0 w-full max-w-md max-h-[min(90dvh,36rem)] flex-col overflow-hidden rounded-lg bg-app-panel shadow-xl dark:bg-gray-800 ring-1 ring-black/10 dark:ring-white/10">
            <div className="min-h-0 flex flex-1 flex-col overflow-x-hidden overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-3 mb-4 shrink-0 min-w-0">
                <h3 className="min-w-0 flex-1 text-lg font-medium leading-snug text-gray-900 dark:text-white">
                  Manage Users for {zones?.find((z) => z.zone_id === selectedZoneForUsers)?.zone_name}
                </h3>
                <button
                  type="button"
                  onClick={() => setSelectedZoneForUsers(null)}
                  className="shrink-0 text-gray-400 hover:text-gray-500"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400 leading-snug shrink-0">
                Viewers only see zones that list them below. Leave the assignment list empty so this zone stays visible to everyone in your company; add users to hide it from everyone else.
              </p>

              {usersLoading ? (
                <div className="text-center py-4">Loading users...</div>
              ) : users ? (
                <div className="space-y-4">
                  <div className="min-w-0">
                    <label htmlFor="zone-add-user" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                      Add user to this zone
                    </label>
                    <div className="flex w-full flex-col gap-2 min-w-0">
                      <div className="min-w-0 w-full shrink-0">
                        <select
                          id="zone-add-user"
                          value={pendingUserId}
                          onChange={(e) => setPendingUserId(e.target.value)}
                          disabled={isSavingZoneUsers}
                          className="block w-full min-w-0 max-w-full truncate rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm outline-none disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="">Select a user…</option>
                          {users
                            .filter((u) => {
                              const z = zones?.find((x) => x.zone_id === selectedZoneForUsers);
                              return !isUserAssignedToZone(getAssignedUserIds(z), u.id);
                            })
                            .map((u) => (
                              <option key={u.id} value={String(u.id)}>
                                {u.firstName} {u.lastName} ({u.email})
                              </option>
                            ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        disabled={!pendingUserId || isSavingZoneUsers}
                        onClick={() => void handleAddPendingUserToZone()}
                        className="inline-flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <UserPlus className="h-4 w-4" />
                        {isSavingZoneUsers ? 'Saving…' : 'Add to zone'}
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Your changes are saved as soon as you add or remove someone.
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Users assigned to this zone
                    </h4>
                    <div className="max-h-48 space-y-2 overflow-y-auto overflow-x-hidden">
                      {users.filter((user) => {
                        const z = zones?.find((x) => x.zone_id === selectedZoneForUsers);
                        return isUserAssignedToZone(getAssignedUserIds(z), user.id);
                      }).length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 italic py-2">No users assigned yet — zone is visible to everyone in your company.</p>
                      ) : (
                        users
                          .filter((user) => {
                            const z = zones?.find((x) => x.zone_id === selectedZoneForUsers);
                            return isUserAssignedToZone(getAssignedUserIds(z), user.id);
                          })
                          .map((user) => (
                            <div key={user.id} className="flex min-w-0 items-center justify-between gap-2 rounded-md bg-gray-50 p-2 dark:bg-gray-700">
                              <div className="min-w-0 flex-1">
                                <span className="font-medium text-gray-900 dark:text-white">{user.firstName} {user.lastName}</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400 block truncate sm:inline sm:ml-2">{user.email}</span>
                              </div>
                              <button
                                type="button"
                                disabled={isSavingZoneUsers}
                                onClick={() => void handleRemoveUser(user.id)}
                                className="shrink-0 rounded-md p-1 text-red-600 hover:bg-red-50 hover:text-red-800 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                title="Remove from zone"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">No users found</div>
              )}
            </div>

            <div className="shrink-0 border-t border-gray-200 px-6 py-4 dark:border-gray-700 flex justify-center sm:justify-end">
              <button
                type="button"
                onClick={() => setSelectedZoneForUsers(null)}
                className="inline-flex min-h-[2.75rem] w-full max-w-full items-center justify-center rounded-md bg-indigo-600 px-5 text-sm font-medium text-white hover:bg-indigo-700 sm:h-10 sm:min-h-0 sm:w-auto sm:max-w-none"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}